/**
 * @genesis_domain Auth Engine - OTP Verification Form
 * @genesis_version V9.1
 * @genesis_intent Bento UI 8-digit OTP verification form. Renders when email confirmation is required
 *   after registration. Uses native React 19 <form action={formAction}> with useFormStatus.
 * @genesis_boundaries No business logic, no direct API calls. Pure presentational component.
 * @genesis_ai_trail V1.0: Created during Feature Sprint 1.7 — 8-Digit OTP Verification Flow.
 *   Extracted from RegistrationForm.tsx to keep main form under 250-line limit.
 * @genesis_ai_trail V1.1: Global Semantic Strike — eradicated all literal Tailwind colors,
 *   all dark: modifiers. Replaced with var(--color-*) semantic tokens.
 */

import { Mail, ShieldCheck } from 'lucide-react';
import { useFormStatus } from 'react-dom';

function VerifyButton() {
    const { pending } = useFormStatus();
    return (
        <button
            type="submit"
            disabled={pending}
            className="w-full px-6 py-3 text-[var(--color-content-inverse)] font-semibold rounded-[16px]
                       bg-[var(--color-primary-base)] hover:bg-[var(--color-primary-hover)]
                       disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
        >
            {pending ? (
                <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Verifying...
                </span>
            ) : (
                <span className="flex items-center justify-center gap-2">
                    <ShieldCheck className="w-5 h-5" />
                    Verify Email
                </span>
            )}
        </button>
    );
}

interface OTPVerificationProps {
    email: string;
    formAction: (formData: FormData) => void;
    isPending: boolean;
    error: string | null;
}

export function OTPVerification({ email, formAction, isPending, error }: OTPVerificationProps) {
    return (
        <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-[var(--color-primary-subtle)]
                            flex items-center justify-center mb-6">
                <Mail className="w-8 h-8 text-[var(--color-primary-base)]" />
            </div>

            <h2 className="text-xl font-bold text-[var(--color-content-primary)] mb-3">
                Check Your Email
            </h2>

            <p className="text-[var(--color-content-secondary)] mb-2 leading-relaxed">
                We sent an 8-digit verification code to
            </p>
            <p className="text-sm font-semibold text-[var(--color-content-primary)] mb-6">
                {email}
            </p>

            {error && (
                <div className="w-full mb-6 px-4 py-3 rounded-[16px]
                                bg-[var(--color-danger-subtle)] border border-[var(--color-danger-subtle)]
                                text-[var(--color-danger-base)] text-sm text-left">
                    {error}
                </div>
            )}

            <form action={formAction} className="w-full space-y-5">
                <input type="hidden" name="email" value={email} />

                <div>
                    <label htmlFor="token" className="block text-sm font-medium text-[var(--color-content-secondary)] mb-1.5">
                        Verification Code
                    </label>
                    <input
                        id="token" name="token" type="text" inputMode="numeric"
                        required maxLength={8} pattern="\d{8}" autoComplete="one-time-code"
                        disabled={isPending} placeholder="00000000"
                        className="w-full px-4 py-3 rounded-[16px] theme-input shadow-inner
                                   border border-[var(--color-input-border)]
                                   text-[var(--color-input-text)] placeholder-[var(--color-content-tertiary)]
                                   focus:outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)]
                                   focus:border-[var(--color-primary-base)]
                                   disabled:opacity-50 disabled:cursor-not-allowed
                                   text-center text-2xl tracking-[0.5em] font-mono transition-all duration-300"
                    />
                    <p className="mt-2 text-xs text-[var(--color-content-tertiary)] text-left">
                        Enter the 8-digit code from your email
                    </p>
                </div>

                <VerifyButton />
            </form>

            <p className="mt-6 text-sm text-[var(--color-content-tertiary)]">
                Didn't receive the code? Check your spam folder or{' '}
                <a href="/login" className="text-[var(--color-primary-base)] hover:underline font-medium">
                    try signing up again
                </a>
                .
            </p>
        </div>
    );
}